import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Badge,
  Typography,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthProvider';
import { db } from '@/firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useFirestore } from '@/hooks/useFirestore';

interface TodoItem {
  id: string;
  type: 'practice' | 'review' | 'weak_subject';
  subject: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  completed: boolean;
}

interface Notification {
  id: string;
  type: 'comment' | 'reply' | 'like';
  questionId: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

const AIRoadmapNotificationPopup: React.FC = () => {
  const { user } = useAuth();
  const firestore = useFirestore();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      loadTodos();
      loadNotifications();
    }
  }, [user]);

  const loadTodos = async () => {
    if (!user) return;

    // サンプルTODOデータ（実際はFirestoreから取得）
    const sampleTodos: TodoItem[] = [
      {
        id: '1',
        type: 'practice',
        subject: '経済学・経済政策',
        title: '今日の診断クイズ',
        description: '弱点克服のための5問',
        priority: 'high',
        completed: false,
      },
      {
        id: '2',
        type: 'weak_subject',
        subject: '財務・会計',
        title: '苦手分野の復習',
        description: '損益計算書の理解',
        priority: 'medium',
        completed: false,
      },
      {
        id: '3',
        type: 'review',
        subject: '企業経営理論',
        title: '前回間違えた問題',
        description: '3問の復習が必要',
        priority: 'medium',
        completed: false,
      },
    ];

    setTodos(sampleTodos);
  };

  const loadNotifications = async () => {
    if (!user) return;

    try {
      // 掲示板のコメント通知を取得
      const notificationsRef = firestore.getCurrentExamCollection('notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', user.uid),
        where('read', '==', false),
        orderBy('timestamp', 'desc'),
        limit(10)
      );

      const snapshot = await getDocs(q);
      const notifData: Notification[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date(),
      })) as Notification[];

      setNotifications(notifData);
      setUnreadCount(notifData.length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      // サンプル通知データ
      const sampleNotifications: Notification[] = [
        {
          id: '1',
          type: 'comment',
          questionId: 'q123',
          message: 'あなたの質問に新しいコメントがあります',
          timestamp: new Date(),
          read: false,
        },
        {
          id: '2',
          type: 'reply',
          questionId: 'q124',
          message: 'あなたのコメントに返信がありました',
          timestamp: new Date(Date.now() - 3600000),
          read: false,
        },
      ];
      setNotifications(sampleNotifications);
      setUnreadCount(sampleNotifications.length);
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      // すでに開いている場合は閉じる
      setAnchorEl(null);
    } else {
      // 閉じている場合は開く
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTodoClick = (todo: TodoItem) => {
    switch (todo.type) {
      case 'practice':
        navigate(`/ai-roadmap/trial/quiz/${encodeURIComponent(todo.subject)}`);
        break;
      case 'weak_subject':
        navigate('/practice/weak-subjects');
        break;
      case 'review':
        navigate('/analytics');
        break;
    }
    handleClose();
  };

  const handleNotificationClick = (notification: Notification) => {
    // 通知を既読にする処理
    setNotifications(prev =>
      prev.map(n => (n.id === notification.id ? { ...n, read: true } : n))
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    // 該当の問題ページに遷移
    // navigate(`/questions/${notification.questionId}`);
    handleClose();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'comment':
        return '💬';
      case 'reply':
        return '↩️';
      case 'like':
        return '👍';
      default:
        return '📢';
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="error">
          <TrendingUpIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="fullWidth"
          >
            <Tab
              icon={<AssignmentIcon />}
              label="TODO"
              iconPosition="start"
            />
            <Tab
              icon={
                <Badge badgeContent={unreadCount} color="error">
                  <ChatBubbleOutlineIcon />
                </Badge>
              }
              label="通知"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {/* TODOタブ */}
          {tabValue === 0 && (
            <List sx={{ p: 0 }}>
              {todos.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    すべてのタスクが完了しました！
                  </Typography>
                </Box>
              ) : (
                todos.map((todo, index) => (
                  <React.Fragment key={todo.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleTodoClick(todo)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {todo.title}
                            </Typography>
                            <Chip
                              label={todo.priority === 'high' ? '優先' : '通常'}
                              size="small"
                              color={getPriorityColor(todo.priority) as any}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {todo.subject}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {todo.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))
              )}
            </List>
          )}

          {/* 通知タブ */}
          {tabValue === 1 && (
            <List sx={{ p: 0 }}>
              {notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    新しい通知はありません
                  </Typography>
                </Box>
              ) : (
                notifications.map((notif, index) => (
                  <React.Fragment key={notif.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton
                      onClick={() => handleNotificationClick(notif)}
                      sx={{
                        bgcolor: notif.read ? 'transparent' : 'action.hover',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{getNotificationIcon(notif.type)}</span>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: notif.read ? 400 : 600 }}
                            >
                              {notif.message}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(notif.timestamp).toLocaleString('ja-JP', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))
              )}
            </List>
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button
            size="small"
            onClick={() => {
              navigate('/ai-roadmap/progress');
              handleClose();
            }}
          >
            学習進捗を見る
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AIRoadmapNotificationPopup;
